"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { Collapsible, CollapsibleContent } from "fumadocs-ui/components/ui/collapsible";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { type SyntheticEvent, useEffect, useReducer, useState, useTransition } from "react";
import { cn } from "../lib/cn";
import { useDocsI18n } from "../lib/i18n";
import { buttonVariants } from "./ui/button";
const rateButtonVariants = cva("inline-flex items-center gap-2 px-3 py-2 rounded-full font-medium border text-sm [&_svg]:size-4 disabled:cursor-not-allowed", {
  variants: {
    active: {
      true: "bg-fd-accent text-fd-accent-foreground [&_svg]:fill-current",
      false: "text-fd-muted-foreground"
    }
  }
});
export interface Feedback {
  opinion: "good" | "bad";
  url?: string;
  message: string;
}
export type ActionResponse = Record<string, never>;
interface Result extends Feedback {
  response?: ActionResponse;
}
function readStoredFeedback(url: string): Result | null {
  const item = localStorage.getItem(`docs-feedback-${url}`);
  return item === null ? null : JSON.parse(item) as Result;
}
export function Feedback(t0) {
  const $ = _c(51);
  const {
    onRateAction
  } = t0;
  const {
    t
  } = useDocsI18n();
  const url = usePathname();
  const [previous, replacePrevious] = useReducer(_temp, null);
  const [opinion, setOpinion] = useState(null);
  const [message, setMessage] = useState("");
  const [isPending, startTransition] = useTransition();
  let t1;
  let t2;
  if ($[0] !== url) {
    t1 = () => {
      replacePrevious(readStoredFeedback(url));
    };
    t2 = [url];
    $[0] = url;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== previous || $[4] !== url) {
    t3 = () => {
      const key = `docs-feedback-${url}`;
      if (previous) {
        localStorage.setItem(key, JSON.stringify(previous));
      } else {
        localStorage.removeItem(key);
      }
    };
    t4 = [previous, url];
    $[3] = previous;
    $[4] = url;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== message || $[8] !== onRateAction || $[9] !== opinion || $[10] !== url) {
    t5 = function submit(e) {
      if (opinion === null) {
        return;
      }
      startTransition(() => {
        const feedback = {
          opinion,
          message
        };
        onRateAction(url, feedback).then(response => {
          replacePrevious({
            response,
            ...feedback
          });
          setMessage("");
          setOpinion(null);
        });
      });
      e?.preventDefault();
    };
    $[7] = message;
    $[8] = onRateAction;
    $[9] = opinion;
    $[10] = url;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const submit = t5;
  const activeOpinion = previous?.opinion ?? opinion;
  const t6 = opinion !== null || previous !== null;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = v => {
      if (!v) {
        setOpinion(null);
      }
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("feedback.prompt");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <p className="text-sm font-medium pe-2">{t8}</p>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = previous !== null;
  let t11;
  if ($[17] !== activeOpinion) {
    t11 = cn(rateButtonVariants({
      active: activeOpinion === "good"
    }));
    $[17] = activeOpinion;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setOpinion("good");
    };
    t13 = <ThumbsUp />;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t) {
    t14 = t("feedback.good");
    $[21] = t;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t14) {
    t15 = <button disabled={t10} className={t11} onClick={t12}>{t13}{t14}</button>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = previous !== null;
  let t17;
  if ($[27] !== activeOpinion) {
    t17 = cn(rateButtonVariants({
      active: activeOpinion === "bad"
    }));
    $[27] = activeOpinion;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setOpinion("bad");
    };
    t19 = <ThumbsDown />;
    $[29] = t18;
    $[30] = t19;
  } else {
    t18 = $[29];
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t) {
    t20 = t("feedback.bad");
    $[31] = t;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t16 || $[34] !== t17 || $[35] !== t20) {
    t21 = <button disabled={t16} className={t17} onClick={t18}>{t19}{t20}</button>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t15 || $[38] !== t21 || $[39] !== t9) {
    t22 = <div className="flex flex-row items-center gap-2">{t9}{t15}{t21}</div>;
    $[37] = t15;
    $[38] = t21;
    $[39] = t9;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== isPending || $[42] !== message || $[43] !== previous || $[44] !== submit || $[45] !== t) {
    t23 = <CollapsibleContent className="mt-3">{previous ? <div className="px-3 py-6 flex flex-col items-center gap-3 bg-fd-card text-fd-muted-foreground text-sm text-center rounded-xl"><p>{t("feedback.thanks")}</p><div className="flex flex-row items-center gap-2"><button className={cn(buttonVariants({
            color: "secondary"
          }), "text-xs")} onClick={() => {
            setOpinion(previous.opinion);
            replacePrevious(null);
          }}>{t("feedback.submitAgain")}</button></div></div> : <form className="flex flex-col gap-3" onSubmit={submit}><textarea autoFocus={true} required={true} value={message} onChange={e_0 => {
          setMessage(e_0.target.value);
        }} className="border rounded-lg bg-fd-secondary text-fd-secondary-foreground p-3 resize-none focus-visible:outline-none placeholder:text-fd-muted-foreground" placeholder={t("feedback.placeholder")} onKeyDown={e_1 => {
          if (!e_1.shiftKey && e_1.key === "Enter") {
            submit(e_1);
          }
        }} /><button type="submit" className={cn(buttonVariants({
          color: "outline"
        }), "w-fit px-3")} disabled={isPending}>{t("feedback.submit")}</button></form>}</CollapsibleContent>;
    $[41] = isPending;
    $[42] = message;
    $[43] = previous;
    $[44] = submit;
    $[45] = t;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t22 || $[48] !== t23 || $[49] !== t6) {
    t24 = <Collapsible open={t6} onOpenChange={t7} className="border-y py-3">{t22}{t23}</Collapsible>;
    $[47] = t22;
    $[48] = t23;
    $[49] = t6;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  return t24;
}
function _temp(_previous, nextPrevious) {
  return nextPrevious;
}